import {
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
} from "../../utils/validationUtils";
import indiaStateCity from "../../constants/indiaStateCity.json";

// ── State + City options from JSON ────────────────────────────────────────────
const STATE_CITY_DATA = indiaStateCity.india_states_cities || [];

export const STATE_OPTIONS = [
  { value: "", label: "Select state" },
  ...STATE_CITY_DATA.map((s) => ({ value: s.state, label: s.state })),
];

/**
 * Returns city options for a given state name.
 * Falls back to empty list if state not found.
 */
export const getCityOptions = (stateName) => {
  if (!stateName) return [{ value: "", label: "Select city" }];
  const found = STATE_CITY_DATA.find(
    (s) => s.state.toLowerCase() === stateName.toLowerCase(),
  );
  if (!found) return [{ value: "", label: "Select city" }];
  return [
    { value: "", label: "Select city" },
    ...found.cities.map((c) => ({ value: c, label: c })),
  ];
};

// KYC form field definitions
export const ENTITY_INFO_FIELDS_BEFORE_GST = [
  {
    name: "parentName",
    label: "Entity Name",
    type: "text",
    placeholder: "Enter entity name",
    required: false,
  },
  {
    name: "tradeName",
    label: "Trade Name",
    type: "text",
    placeholder: "Enter name as per GST certificate",
    required: false,
  },
  {
    name: "pinCode",
    label: "Pin Code",
    type: "text",
    placeholder: "Enter 6-digit pin code",
    required: false,
    maxLength: 6,
    minLength: 6,
    group: "location",
    // pincode API auto-fill is disabled — state/city selected via dropdowns
  },
  {
    name: "State",
    label: "State",
    type: "select",
    required: false,
    options: STATE_OPTIONS,
    group: "location",
  },
  {
    name: "city",
    label: "City",
    type: "select",
    required: false,
    // options resolved dynamically based on selected State — see renderFields override
    options: [{ value: "", label: "Select city" }],
    group: "location",
  },
  {
    name: "address",
    label: "Address",
    type: "textarea",
    required: false,
    placeholder: "Enter complete address",
    rows: 3,
  },
  {
    name: "natureOfBusiness",
    label: "Nature of Business",
    type: "textarea",
    placeholder: "Brief description of Business",
    required: false,
  },
];

export const ENTITY_INFO_FIELDS_AFTER_GST = [
  {
    name: "isInvoiceRequired",
    label: "E Invoice Applicability",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select option" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    name: "entityType",
    label: "Entity Type",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select entity type" },
      { value: "proprietorship", label: "Proprietorship" },
      { value: "partnership", label: "Partnership" },
      { value: "llp", label: "Limited Liability Partnership (LLP)" },
      { value: "private_limited", label: "Private Limited Company" },
      { value: "public_limited", label: "Public Limited Company" },
      { value: "trust", label: "Trust" },
      { value: "society", label: "Society" },
    ],
  },
];

export const ENTITY_INFO_FIELDS = [
  ...ENTITY_INFO_FIELDS_BEFORE_GST,
  ...ENTITY_INFO_FIELDS_AFTER_GST,
];

export const GST_FIELDS = [
  {
    name: "isGSTRegistered",
    label: "GST Registered?",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select option" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
];

export const UTILITY_FIELDS = [
  {
    name: "utilityType",
    label: "Utility Type",
    type: "text",
    placeholder: "e.g. Electricity, Water",
    required: false,
  },
  {
    name: "utilityProviderName",
    label: "Provider Name",
    type: "text",
    placeholder: "Enter provider name",
    required: false,
  },

  {
    name: "utilityAddress",
    label: "Address on Utility Bill",
    type: "textarea",
    placeholder: "Address as on utility bill",
    rows: 2,
    required: false,
  },
];

export const KYC_MSME_TYPE_OPTIONS = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "micro", label: "micro" },
];

export const MSME_FIELDS = [
  {
    name: "isMSMERegistered",
    label: "Registered under MSME?",
    type: "select",
    required: true,
    options: [
      { value: "", label: "Select option" },
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    name: "msmeStatus",
    label: "MSME Status",
    type: "select",
    required: false,
    conditionalOn: "isMSMERegistered",
    conditionalValue: "yes",
    options: [{ value: "", label: "Select option" }, ...KYC_MSME_TYPE_OPTIONS],
  },
];

export const CONTACT_FIELDS = [
  {
    name: "contactPersonName",
    label: "Contact Person Name",
    type: "text",
    placeholder: "Enter name",
    required: false,
  },
  {
    name: "contactPersonDesignation",
    label: "Designation",
    type: "text",
    placeholder: "Enter designation",
    required: false,
  },
  {
    name: "contactPersonMobile",
    label: "Mobile Number",
    type: "tel",
    placeholder: "10-digit mobile",
    required: false,
    maxLength: 10,
    validation: validateMobile,
  },
  {
    name: "contactEmail",
    label: "Contact Person Email ",
    type: "email",
    placeholder: "Enter contact Email ",
    required: false,
    // maxLength: 10,
    // validation: validateMobile,
  },
];

export const BANK_FIELDS = [
  {
    name: "bankName",
    label: "Bank Name",
    type: "text",
    placeholder: "Enter bank name",
    required: false,
  },
  {
    name: "beneficiaryName",
    label: "Beneficiary Name",
    type: "text",
    placeholder: "Enter beneficiary name",
    required: false,
  },
  {
    name: "branchName",
    label: "Branch Name",
    type: "text",
    placeholder: "Enter branch name",
    required: false,
  },
  {
    name: "accountType",
    label: "Account Type",
    type: "select",
    required: false,
    options: [
      { value: "", label: "Select account type" },
      { value: "current", label: "Current" },
    ],
    // proprietorship also allows savings — resolved dynamically in renderFields
    optionsByEntityType: {
      proprietorship: [
        { value: "", label: "Select account type" },
        { value: "savings", label: "Savings" },
        { value: "current", label: "Current" },
      ],
    },
  },
  {
    name: "accountNumber",
    label: "Account Number",
    type: "text",
    placeholder: "Enter account number",
    required: false,
    validation: validateAccountNumber,
  },
  {
    name: "ifscCode",
    label: "IFSC Code",
    type: "text",
    placeholder: "e.g. SBIN0001234",
    required: false,
    maxLength: 11,
    validation: validateIFSC,
  },
];

export const THIRD_PARTY_FIELDS = [
  {
    name: "thirdPartyRelation",
    label: "Third-party Relation with Beneficiary",
    type: "text",
    placeholder: "e.g. Parent, Subsidiary",
    required: false,
  },
];

export const DOCS_BANK = [
  {
    key: "doc_bank_proof",
    label: "Bank Document (Passbook / Cancelled Cheque)",
    required: false,
  },
];

export const ALL_FIELDS = [
  ...ENTITY_INFO_FIELDS,
  ...GST_FIELDS,
  ...MSME_FIELDS,
  ...CONTACT_FIELDS,
  ...BANK_FIELDS,
];

// ── Entity-type document requirements ─────────────────────────────────────────
// Each entry: { key: string (used as file state key), label: string }

export const ENTITY_TYPE_DOCUMENTS = {
  // ── Proprietorship ──────────────────────────────────────────────────────────
  proprietorship: [
    {
      key: "doc_aadhar",
      label: "Aadhar Card",
      required: true,
      inputField: {
        name: "doc_aadhar_number",
        placeholder: "Enter Aadhar number",
        // maxLength: 12,
      },
    },
    {
      key: "aadharCardBackPath",
      label: "Aadhar Card (Back)",
      required: false,
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],

  // ── Partnership ─────────────────────────────────────────────────────────────
  partnership: [
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      required: false,
      // upload only — no inputField
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Primary Partner",
      required: true,
      inputField: {
        name: "doc_aadharBoth_numbers",
        placeholder: "Enter primary partner Aadhar number",
      },
    },
    {
      key: "aadharCardBackPath",
      label: "Aadhar of Primary Partner (Back)",
      required: false,
    },
    {
      key: "doc_aadharBothSecondary",
      label: "Aadhar of Secondary Partner",
      required: false,
      inputField: {
        name: "doc_aadharBothSecondary_numbers",
        placeholder: "Enter secondary partner Aadhar number",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],

  // ── LLP ─────────────────────────────────────────────────────────────────────
  llp: [
    {
      key: "doc_llpCertificate",
      label: "LLP Certificate",
      required: false,
      inputField: {
        name: "doc_llpCertificate_number",
        placeholder: "Enter LLP certificate number",
      },
    },
    {
      key: "doc_partnershipDeed",
      label: "Partnership Deed",
      required: false,
      // upload only — no inputField
    },
    {
      key: "doc_aadharBoth",
      label: "Aadhar of Primary Partner",
      required: true,
      inputField: {
        name: "doc_aadharBoth_numbers",
        placeholder: "Enter primary partner Aadhar number",
      },
    },
    {
      // only adhar back side image
      key: "aadharCardBackPath",
      label: "Aadhar of Primary Partner (Back)",
      required: false,
      // inputField: {
      //   name: "doc_aadharBoth_numbers",
      //   placeholder: "Enter primary partner Aadhar number",
      // },
    },
    {
      key: "doc_aadharBothSecondary",
      label: "Aadhar of Secondary Partner",
      required: false,
      inputField: {
        name: "doc_aadharBothSecondary_numbers",
        placeholder: "Enter secondary partner Aadhar number",
      },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
  ],

  // ── Private Limited ─────────────────────────────────────────────────────────
  private_limited: [
    {
      key: "doc_coi",
      label: "Certificate of Incorporation (COI)",
      required: false,
      inputField: { name: "doc_coi_number", placeholder: "Enter COI number" },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorPan",
      label: "Director PAN",
      required: false,
      inputField: {
        name: "doc_directorPan_number",
        placeholder: "Enter Director PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      required: true,
      inputField: {
        name: "doc_directorAadhar_number",
        placeholder: "Enter Director Aadhar number",
        maxLength: 12,
      },
    },
  ],

  // ── Public Limited ──────────────────────────────────────────────────────────
  public_limited: [
    {
      key: "doc_coi",
      label: "Certificate of Incorporation (COI)",
      required: false,
      inputField: { name: "doc_coi_number", placeholder: "Enter COI number" },
    },
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorPan",
      label: "Director PAN",
      required: false,
      inputField: {
        name: "doc_directorPan_number",
        placeholder: "Enter Director PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_directorAadhar",
      label: "Director Aadhar",
      required: true,
      inputField: {
        name: "doc_directorAadhar_number",
        placeholder: "Enter Director Aadhar number",
        maxLength: 12,
      },
    },
  ],

  // ── Trust ───────────────────────────────────────────────────────────────────
  trust: [
    {
      key: "doc_trustDeed",
      label: "Trust Deed",
      required: false,
      inputField: {
        name: "doc_trustDeed_ref",
        placeholder: "Enter trust deed reference / date",
      },
    },
    {
      key: "doc_panTrust",
      label: "PAN of Trust",
      required: true,
      inputField: {
        name: "doc_panTrust_number",
        maxLength: 10,
        placeholder: "Enter Trust PAN number",
      },
    },
    {
      key: "doc_aadharTrustee",
      label: "Aadhar of Trustee",
      required: true,
      inputField: {
        name: "doc_aadharTrustee_number",
        placeholder: "Enter Trustee Aadhar number",
        maxLength: 12,
      },
    },
    {
      key: "doc_panTrustee",
      label: "PAN of Trustee",
      required: false,
      inputField: {
        name: "doc_panTrustee_number",
        placeholder: "Enter Trustee PAN number",
        maxLength: 10,
      },
    },
  ],

  // ── Society ─────────────────────────────────────────────────────────────────
  society: [
    {
      key: "doc_pan",
      label: "PAN Card",
      required: true,
      inputField: {
        name: "doc_pan_number",
        placeholder: "Enter PAN number",
        maxLength: 10,
      },
    },
    {
      key: "doc_aadhar",
      label: "Aadhar Card",
      required: true,
      inputField: {
        name: "doc_aadhar_number",
        placeholder: "Enter Aadhar number",
        maxLength: 12,
      },
    },
    {
      key: "aadharCardBackPath",
      label: "Aadhar Card (Back)",
      required: false,
    },
  ],
};

// Re-export validateGST so it's accessible from this module
export { validateGST };

// ── Maps internal doc keys → API DocumentType values ──────────────────────────
export const DOC_KEY_TO_API_TYPE = {
  // Entity-type docs
  doc_aadhar: "AadharCardPath",
  aadharCardBackPath: "AadharCardBackPath",
  doc_aadhar_back: "AadharCardBackPath",
  doc_pan: "PANCardPath",
  doc_partnershipDeed: "PartnershipDeedPath",
  doc_llpCertificate: "UploadLLPCertificatePath",
  doc_aadharBoth: "AadharOfBothPartnersPath",
  doc_coi: "CertificateOfIncorporationPath",
  doc_directorPan: "DirectorPANPath",
  doc_directorAadhar: "DirectorAadharPath",
  doc_trustDeed: "TrustDeedPath",
  doc_panTrust: "PANOfTrustPath",
  doc_aadharTrustee: "AadharOfTrusteePath",
  doc_panTrustee: "PANOfTrusteePath",

  // Secondary partner aadhar
  doc_aadharBothSecondary: "AadharOfBothPartnersPathSecond",

  // Conditional docs
  doc_gst_certificate: "GSTCertificatePath",
  doc_utility_bill: "UtilityBillDocumentPath",
  doc_msme_certificate: "MSMECertificatePath",

  // Bank docs
  doc_bank_proof: "PassbookOrCancelledChequePath",

  // GST file (per entry)
  gst_file: "GSTCertificatePath",

  // MSME file
  msme_file: "MSMECertificatePath",

  // Utility bill file
  utility_file: "UtilityBillDocumentPath",

  // Third party / signed form
  third_party_file: "UploadSignedFormPath",
};
